import CloseIcon from '@mui/icons-material/Close';
import { Avatar, Box, Grid, Link, Stack, Typography } from '@mui/material';
import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { ICandidate, amplitude, IUserDTO, findByName, getJobByJobCode, IJob } from 'wisestep-api';
import {
  Button,
  WsColors,
  WsElementWithPopOver,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  IConversationState,
  Ellipse,
} from 'wisestep-base';

import { useUserContext } from '../../../../context';
import { useStatusActionPipelineContext } from '../../../../context/StatusActionPipelineContext';
import { getCandidateLinkedInUrl } from '../../../../features/candidateLayout/utils/CandidateHelper';
import useGetJob from '../../../../features/emailSettings/hooks/useGetJob';
import Call from '../../../../images/Call.svg';
import Hash from '../../../../images/Hash.svg';
import HourGlass from '../../../../images/HourGlass.svg';
import Mail from '../../../../images/Mail.svg';
import Manager from '../../../../images/Manager.svg';
import MapPin from '../../../../images/MapPin.svg';
import QuestionAnswer from '../../../../images/QuestionAnswer.svg';
import UserRound from '../../../../images/UserRound.svg';
import UserRoundCheck from '../../../../images/UserRoundCheck.svg';
import UserRoundTeam from '../../../../images/UserRoundTeam.svg';
import WorldSearch from '../../../../images/WorldSearch.svg';
import { getAccountManagerAnalyticsUrl } from '../../../candidates/utils/CandidateHelper';
import ConversationWidgetDrawer from '../../../conversationWidget/components/ConversationWidgetDrawer';
import { isTeamMember } from '../../../jobs/listing/components/jobCardFooter/PostedBy';
import { generateCandidateViewUrl } from '../../utils/Helper';

import AiMatchScoreButton from './AIMatchScoreButton';
import ListItemButton, { ListType } from './ListItemButtons';

interface ICandidateTooltip {
  candidate: ICandidate;
  onClose: (action: boolean) => void;
  ids: string[];
}

interface CandidateInfoFields {
  key: string;
  value: string;
  icon: string;
  clickable: boolean;
  link: boolean;
  hover: boolean;
  onClick?: () => void;
  url?: string;
}

const CandidateTooltip = ({ candidate, onClose, ids }: ICandidateTooltip) => {
  const [ansCount, setAnsCount] = useState<number>(0);
  const navigate = useNavigate();
  const { job } = useGetJob(candidate?.jobId ?? '');

  useEffect(() => {
    if (candidate?.questionResponse) {
      let count = 0;
      candidate?.questionResponse.forEach((item) => {
        if (item.answer === 'yes' || item.answer === 'no') count++;
      });
      setAnsCount(count);
    }
  }, [candidate]);
  const processedData: CandidateInfoFields[] = [
    {
      key: 'Email ID',
      value: candidate?.email ?? 'NA',
      icon: Mail,
      clickable: false,
      link: false,
      hover: true,
    },
    {
      key: 'Phone',
      value: candidate?.phoneNumber ?? 'NA',
      icon: Call,
      clickable: false,
      link: false,
      hover: true,
    },
    {
      key: 'Job Code',
      value: candidate?.jobCode ?? 'NA',
      icon: Hash,
      clickable: false,
      link: false,
      hover: false,
    },
    {
      key: 'Client Job Code',
      value: candidate?.clientJobCode ?? 'NA',
      icon: Hash,
      clickable: false,
      link: false,
      hover: false,
    },
    candidate?.jobClientRecruiterName && !/.*xxxxxx.*/.test(candidate.jobClientRecruiterName)
      ? {
          key: 'Client Spoc',
          value: candidate?.jobClientRecruiterName ?? 'NA',
          icon: UserRound,
          clickable: true,
          link: true,
          hover: false,
          onClick: async () => {
            const jobResponse = candidate.jobCode && (await getJobByJobCode(candidate.jobCode));
            const job: IJob = jobResponse && jobResponse.data;
            navigate(
              `/${job?.clientContactId}/client-contact-details?isChild=true&name=${
                job?.companyName
              }&goBack=true&clientId=${job?.clientId ?? ''}`
            );
          },
        }
      : {
          key: 'Client Spoc',
          value: candidate?.jobClientRecruiterName ?? 'NA',
          icon: UserRound,
          link: false,
          clickable: false,
          hover: false,
        },
    {
      key: 'Account Manager',
      value: candidate.jobAccountManagerName ?? 'NA',
      icon: Manager,
      clickable: true,
      link: false,
      hover: false,
      onClick: async () => {
        if (candidate.jobAccountManagerEmail) {
          const url = await getAccountManagerAnalyticsUrl(candidate.jobAccountManagerEmail);
          url && navigate(url);
        }
      },
    },
    {
      key: 'Job Location',
      value: `${candidate?.jobLocations?.[0]?.city ?? ''}, ${
        candidate?.jobLocations?.[0]?.state ?? ''
      }, ${candidate?.jobLocations?.[0]?.country ?? ''}`,
      icon: MapPin,
      clickable: false,
      link: false,
      hover: false,
    },
    {
      key: 'Candidate Location',
      value: `${candidate?.currentLocation?.[0]?.city ?? candidate?.locations?.[0]?.city ?? ''}, ${
        candidate?.currentLocation?.[0]?.state ?? candidate?.locations?.[0]?.state ?? ''
      }, ${candidate?.currentLocation?.[0]?.country ?? candidate?.locations?.[0]?.country ?? ''}`,
      icon: MapPin,
      clickable: false,
      link: false,
      hover: false,
    },
    {
      key: 'Source',
      value: candidate?.source ?? 'NA',
      icon: WorldSearch,
      clickable: false,
      link: false,
      hover: false,
    },
    {
      key: 'Created By',
      value: candidate?.recruiterName ?? 'NA',
      icon: UserRoundCheck,
      clickable: true,
      link: true,
      url: `/recruiter/profile?email=${candidate.createdBy}&isLink=true&&goBackUrl=/all-submissions&isUserAnalytics=true`,
      hover: false,
    },
    {
      key: 'Submitted By Team',
      value: candidate?.modifiedByTeamName ?? 'NA',
      icon: UserRoundTeam,
      clickable: !!candidate?.modifiedByTeamName,
      link: false,
      onClick: async () => {
        try {
          if (candidate.modifiedByTeamName) {
            const team = await findByName(candidate.modifiedByTeamName);
            const teamId = team.data?.data?.id;
            const hasAccess = teamId && (await isTeamMember(teamId));
            navigate('/team/team-members-view', {
              state: {
                teamId: teamId,
                teamName: candidate.modifiedByTeamName,
                hasAccess: hasAccess,
              },
            });
          }
        } catch (error) {
          console.error('something went wrong while fetching data', error);
        }
      },
      hover: false,
    },
    {
      key: 'Turn Arround Time',
      value: `${candidate?.turnAroundTime?.days} days`,
      icon: HourGlass,
      clickable: false,
      link: false,
      hover: false,
    },
    {
      key: 'Recruiter questions',
      value: `${ansCount} responses`,
      icon: QuestionAnswer,
      clickable: true,
      link: false,
      hover: false,
    },
  ];

  const closeTooltip = () => {
    onClose(false);
  };

  const loggedInUser = useUserContext().userDTO as IUserDTO;
  const statusActionPipeline = useStatusActionPipelineContext();
  const conversationStateValue: IConversationState = {
    candidate: candidate,
    currentUser: loggedInUser,
    candidateLocation: null,
    candidatePreferredLocations: [],
    recentJobMappings: [],
    autoSaveDisabledSettings: null,
    autoSaveAllowed: true,
    getDailyConversationCount: null,
    totalConversationPermanentCount: null,
    messageAndInteractionPipeline: statusActionPipeline,
  };
  const [open, setOpenWidget] = useState(false);
  return (
    <Grid container spacing={2} sx={{ padding: '14px 14px' }}>
      <Grid item xs={12} display="flex" justifyContent="space-between">
        <Box display="flex" alignItems="center" columnGap={1} sx={{ cursor: 'pointer', my: 1 }}>
          <Avatar>{candidate.name[0]}</Avatar>
          <RouterLink
            to={generateCandidateViewUrl(candidate, ids)}
            style={{ textDecoration: 'none' }}
          >
            <WsTypography
              cursor="pointer"
              variant={WsTypoVariants.SUB_TITLE}
              color={WsTypoColors.PRIMARY600}
              onClick={() => {
                amplitude('CandidateView_Redirect_Submission', {
                  clickedOn: 'Redirect Submission',
                  pageName: 'candidate-layout',
                  pageUrl: window.location.href,
                });
              }}
            >
              {candidate?.name ?? 'NA'}
            </WsTypography>
          </RouterLink>
          <Ellipse color={WsColors.TertiaryShades600} />
          <AiMatchScoreButton
            job={job}
            email={candidate?.email ?? ''}
            linkedInUrl={getCandidateLinkedInUrl(
              candidate?.linkedInURL ?? '',
              candidate?.profileURLs ?? []
            )}
          />
        </Box>
        <Grid
          item
          display="flex"
          alignItems="center"
          xs={6}
          justifyContent="flex-end"
          sx={{ marginTop: -1 }}
        >
          <Button onClick={closeTooltip}>
            <CloseIcon sx={{ color: WsColors.GenericRichBlack, fontWeight: 'bold' }} />
          </Button>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          {processedData.map((data, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Box display="flex" alignItems="center" marginLeft={1}>
                <img src={data.icon} alt="Frame Logo" color={WsColors.TertiaryShades900} />
                <Typography
                  variant="body1"
                  sx={{
                    color: WsColors.TertiaryShades900,
                    width: 150,
                    marginLeft: 2,
                    fontWeight: 'medium',
                  }}
                >
                  {data.key}:
                </Typography>
                {data.hover ? (
                  <>
                    <WsElementWithPopOver
                      element={
                        <WsTypography
                          variant={WsTypoVariants.BODY1}
                          color={
                            data.hover
                              ? WsTypoColors.PRIMARY600
                              : data.value === 'NA'
                              ? WsTypoColors.GREYTEXT
                              : WsTypoColors.BLACK
                          }
                        >
                          {data.value}
                        </WsTypography>
                      }
                    >
                      <Stack p={1}>
                        <ListItemButton
                          value={data.value}
                          type={data.key === 'Phone' ? ListType.PHONE : ListType.EMAIL}
                          setOpenWidget={setOpenWidget}
                        />
                      </Stack>
                    </WsElementWithPopOver>
                  </>
                ) : data.link ? (
                  <Link
                    href={
                      data?.url
                        ? data.url
                        : `/${job.clientContactId}/client-contact-details?isChild=true&name=${
                            job.companyName
                          }&goBack=true&clientId=${job.clientId ?? ''}&goBackUrl=/all-submissions`
                    }
                    underline={'none'}
                  >
                    <WsTypography
                      variant={WsTypoVariants.BODY1}
                      color={
                        data.clickable
                          ? WsTypoColors.PRIMARY600
                          : data.value === 'NA'
                          ? WsTypoColors.GREYTEXT
                          : WsTypoColors.BLACK
                      }
                      cursor={data.onClick && 'pointer'}
                    >
                      {data.value}
                    </WsTypography>
                  </Link>
                ) : (
                  <WsTypography
                    variant={WsTypoVariants.BODY1}
                    color={
                      data.clickable
                        ? WsTypoColors.PRIMARY600
                        : data.value === 'NA'
                        ? WsTypoColors.GREYTEXT
                        : WsTypoColors.BLACK
                    }
                    onClick={() => data.onClick?.()}
                    cursor={data.onClick && 'pointer'}
                  >
                    {data.value}
                  </WsTypography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Grid>
      <ConversationWidgetDrawer
        open={open}
        aggregation={null}
        recruiterAction={''}
        selectedJob={null}
        closeDrawer={() => {
          setOpenWidget(false);
        }}
        submissionPurpose={''}
        state={conversationStateValue}
      />
    </Grid>
  );
};

export default CandidateTooltip;
